/**
 * Client-side checks for ingredient community notes.
 * Keep moderation rules aligned with netlify/functions/submit-ingredient-note.js
 */

import { ALL_INGREDIENTS } from "./ingredients";

export const INGREDIENT_NOTE_ALLOWED_IDS = new Set(ALL_INGREDIENTS.map((r) => r.id));

const PROFANITY = [
  "fuck",
  "shit",
  "cunt",
  "bitch",
  "bastard",
  "piss",
  "wank",
  "cock",
  "dick",
  "slut",
  "whore",
  "nigger",
  "nigga",
  "spic",
  "chink",
  "retard",
];

const MEDICAL_BLOCK_RE =
  /\b(cures?|cured|heals?|healed|healing|revers(e|ed|ing)|treat|treats|treatment|guaranteed|miracle|doctor\s+replacement|cancer|diabetes|blood\s+pressure\s+cure|parasite\s+cleanse|detox\s*parasites?)\b/i;

const SENSITIVE_REVIEW = [
  /\bpregnanc(y|ies)\b/i,
  /\bpregnant\b/i,
  /\bbreast\s*feeding\b/i,
  /\bbreastfeeding\b/i,
  /\bthyroid\b/i,
  /\bblood\s*thinner/i,
  /\bmedication(s)?\b/i,
  /\bchronic\s+illness\b/i,
  /\bchronic\s+disease\b/i,
  /\bserious\s+condition\b/i,
  /\bdiagnosed\b/i,
  /\bliver\s+(disease|failure)\b/i,
  /\bchild('?s)?\s+dos(e|ing)\b/i,
  /\bfor\s+my\s+child\b/i,
  /\bfor\s+children\b/i,
  /\bkids?\b.*\b(dos|dose|spoon|take)\b/i,
  /\bbaby\b.*\b(dos|dose|give)\b/i,
];

function normalize(s) {
  return String(s || "")
    .replace(/\s+/g, " ")
    .trim();
}

function containsProfanity(text) {
  const lower = text.toLowerCase();
  return PROFANITY.some((w) => {
    const re = new RegExp(`\\b${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
    return re.test(lower);
  });
}

/**
 * @param {{ displayName: string, location: string, comment: string }} p
 * @returns {{ ok: true, warnSensitive?: boolean } | { ok: false, error: string }}
 */
export function preflightIngredientNote(p) {
  const displayName = normalize(p.displayName);
  const location = normalize(p.location);
  const comment = normalize(p.comment);
  const blob = `${displayName}\n${location}\n${comment}`;

  if (containsProfanity(blob)) {
    return { ok: false, error: "Language not suitable for the community board." };
  }
  if (MEDICAL_BLOCK_RE.test(blob)) {
    return { ok: false, error: "Please avoid medical outcome claims. Share kitchen or routine wording only." };
  }
  for (const re of SENSITIVE_REVIEW) {
    if (re.test(blob)) {
      return { ok: true, warnSensitive: true };
    }
  }
  return { ok: true };
}

export function isIngredientNoteTargetAllowed(ingredientId) {
  return INGREDIENT_NOTE_ALLOWED_IDS.has(String(ingredientId || "").trim());
}
